import { useEffect, useState, type FormEvent } from 'react';
import { Search, X, SlidersHorizontal, Bookmark, BookmarkPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface SearchBarProps {
  /** Current keyword (controlled). Empty string when no query is active. */
  value: string;
  /** Called when the user submits (Enter or clicks the search icon). */
  onSubmit: (next: string) => void;
  /** Called as the user types, before the keyword is committed to URL state. */
  onDraftChange?: (next: string) => void;
  /** Optional placeholder. Default: "Search…". */
  placeholder?: string;
  /** If provided, a Filters button toggles the filter panel. */
  onToggleFilters?: () => void;
  /** Count of active filters; renders a small dot on the Filters button. */
  activeFiltersCount?: number;
  /** If provided, a "Saved" button is shown. */
  onOpenSaved?: () => void;
  /** Show "Save this search" affordance when there's an active query. */
  onSave?: () => void;
  /** Disable the input + buttons. */
  disabled?: boolean;
  className?: string;
}

/**
 * Source-agnostic search bar. Controlled — parent owns the canonical
 * keyword (typically driven from the URL state). The bar maintains its
 * own *draft* state so typing doesn't trigger a re-fetch on every keystroke;
 * submitting (Enter or magnifying glass) commits the draft via `onSubmit`.
 *
 * The Filters / Saved buttons are opt-in: pass the handlers to enable.
 */
export function SearchBar({
  value,
  onSubmit,
  onDraftChange,
  placeholder = 'Search…',
  onToggleFilters,
  activeFiltersCount,
  onOpenSaved,
  onSave,
  disabled,
  className,
}: SearchBarProps) {
  const [draft, setDraft] = useState(value);

  // Keep the input in sync when the URL changes externally (e.g. back nav).
  useEffect(() => {
    setDraft(value);
    onDraftChange?.(value);
  }, [onDraftChange, value]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && e.currentTarget.contains(activeElement)) {
      activeElement.blur();
    }
    onSubmit(draft.trim());
  }

  function handleDraftChange(next: string) {
    setDraft(next);
    onDraftChange?.(next);
  }

  function handleClear() {
    handleDraftChange('');
    onSubmit('');
  }

  const hasActiveFilters = (activeFiltersCount ?? 0) > 0;

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('flex items-center gap-2', className)}
      role="search"
    >
      <div className="relative min-w-0 flex-1">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          value={draft}
          onChange={(e) => handleDraftChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-8 pr-8"
          aria-label="Search"
        />
        {draft && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-accent/40 hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <Button
        type="submit"
        size="icon"
        disabled={disabled}
        className="h-10 w-10 shrink-0"
        aria-label="Search"
      >
        <Search className="h-4 w-4" />
      </Button>

      {onToggleFilters && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onToggleFilters}
          disabled={disabled}
          className="relative h-10 w-10 shrink-0"
          aria-label="Filters"
        >
          <SlidersHorizontal className="h-4 w-4" />
          {hasActiveFilters && (
            <span
              className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground"
              aria-label={`${activeFiltersCount} active filters`}
            >
              {activeFiltersCount}
            </span>
          )}
        </Button>
      )}

      {onOpenSaved && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onOpenSaved}
          disabled={disabled}
          className="h-10 w-10 shrink-0"
          aria-label="Saved"
        >
          <Bookmark className="h-4 w-4" />
        </Button>
      )}

      {onSave && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onSave}
          disabled={disabled}
          className="h-10 w-10 shrink-0"
          aria-label="Save"
        >
          <BookmarkPlus className="h-4 w-4" />
        </Button>
      )}
    </form>
  );
}
